import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Spinner, Card, CardBody } from "reactstrap";
import { t } from "i18next";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

import { getDeal } from "../../../api/deal";
import { getLeadSource } from "../../../api/leadSource";
import { getAdmin } from "../../../api/admins";
import { getBusinessCategory } from "../../../api/businessCategory";

import DealLogs from "../deal/DealLogs";
import DealFeedback from "./DealFeedback";
import DealDeliverable from "./DealDeliverable";

export default function DealShow() {
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [leadSource, setLeadSource] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [businessCategory, setBusinessCategory] = useState("");
  const [service, setService] = useState("");

  const currentUser = useMemo(() => {
    return JSON.parse(localStorage.getItem("currentUser")) || {};
  }, []);

  const role = currentUser.role || currentUser.roleName || "";

  const hasPermission = (action) => {
    if (role === "super admin" || role?.name === "super admin") return true;
    return (
      role.permissions &&
      role.permissions.some(
        (perm) => perm.resource === "Sales" && perm.actions.includes(action)
      )
    );
  };

  const canRead = () => hasPermission("update") || hasPermission("delete");
  const canCreate = () => hasPermission("create");
  useEffect(() => {
    if (!canRead()) {
      setHasAccess(false);
      setLoading(false);
      return;
    }
    setHasAccess(true);

    const fetchDealData = async () => {
      try {
        const res = await getDeal(id);
        setDeal(res);
        const leadId = res?.data?.leadId;

        if (leadId?.leadSource) {
          const leadSourceRes = await getLeadSource(
            leadId.leadSource._id || leadId.leadSource
          );
          setLeadSource(leadSourceRes?.data?.name || "");
        }

        if (leadId?.assignedTo) {
          const assignedToRes = await getAdmin(
            leadId.assignedTo._id || leadId.assignedTo
          );
          setAssignedTo(assignedToRes?.data?.name || "");
        }

        if (leadId?.businessCategory) {
          const categoryId =
            leadId.businessCategory._id || leadId.businessCategory;
          const categoryRes = await getBusinessCategory(categoryId);
          setBusinessCategory(categoryRes?.data?.name || "");
        }

        const services = res?.data?.services || [];
        if (services.length > 0) {
          const allServiceNames = services
            .map((s) => (Array.isArray(s.names) ? s.names[0] : s.name || ""))
            .filter(Boolean)
            .join(", ");
          setService(allServiceNames);
        }
      } catch (err) {
        console.error("Error fetching deal or related data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDealData();
  }, [id]);

  if (loading) return <Spinner color="primary" />;

  if (!hasAccess) {
    return <div>{t("You do not have permission to view this deal.")}</div>;
  }

  if (!deal) return <div>{t("Deal not found.")}</div>;

  const dealData = deal.data;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title={t("Deal Details")} addUrl={id ? `/deals` : null} />

        <Card className="mt-4">
          <CardBody>
            <p>
              <strong>{t("Lead Name")}:</strong>{" "}
              {dealData.leadId?.name || t("No lead assigned")}
            </p>
            <p>
              <strong>{t("Lead Email")}:</strong>{" "}
              {dealData.leadId?.email || t("N/A")}
            </p>
            <p>
              <strong>{t("Lead Phone")}:</strong>{" "}
              {dealData.leadId?.phone || t("N/A")}
            </p>
            <p>
              <strong>{t("Lead WhatsApp")}:</strong>{" "}
              {dealData.leadId?.whatsApp || t("N/A")}
            </p>
            {/* <p>
              <strong>{t("Lead Source")}:</strong>{" "}
              {leadSource || t("N/A")}
            </p>
            <p>
              <strong>{t("Assigned To")}:</strong>{" "}
              {assignedTo || t("N/A")}
            </p>
            <p>
              <strong>{t("Service")}:</strong>{" "}
              {service || t("No service assigned")}
            </p>
            <p>
              <strong>{t("Business Category")}:</strong>{" "}
              {businessCategory || t("N/A")}
            </p> */}
            <p>
              <strong>{t("Status")}:</strong>{" "}
              {dealData.StatusDeal?.name || t("No status assigned")}
            </p>
            <p className="mb-9">
              <strong>{t("Budget")}:</strong>{" "}
              {dealData.budget
                ? `${dealData.budget.toLocaleString()} EGP`
                : t("N/A")}
            </p>
            <div className="mb-20">
              <Breadcrumbs
                title={`${t("Log")} ${t("Table")}`}
                addUrl={id ? `/logs/${id}` : null}
                hasAddBtn={canCreate()}
                className="mb-4"
                isMainPage={true}
              />
              <DealLogs dealId={id} />
            </div>
            <hr className="my-10 border-t border-gray-200" />
            <br className="my-20" />
            <div className="mt-10">
              <Breadcrumbs
                title={`${t("Feedback")} ${t("Table")}`}
                addUrl={id ? `/feedback/add/${id}` : null}
                hasAddBtn={canCreate()}
                className="mb-4"
                isMainPage={true}
              />
              <DealFeedback dealId={id} />
            </div>
            <hr className="my-10 border-t border-gray-200" />
            <br className="my-20" />
            <div className="mt-10">
              <Breadcrumbs
                title={t("Deliverable Table")}
                addUrl={id ? `/deliverables/add/${id}` : null}
                hasAddBtn={canCreate()}
                className="mb-4"
                isMainPage={true}
              />
              <DealDeliverable dealId={id} />
            </div>
            <hr className="my-10 border-t border-gray-200" />
            <br className="my-20" />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
